import { computed, inject, Service, type Signal, signal } from '@angular/core';
import { catchError, type Observable, of, tap } from 'rxjs';

import type { IBasicChartConfigurations, ICompletionStatus, INotification } from '../models';
import { DashboardService } from '../repository/dashboard.service';
import {
  type DashboardChartSummaries,
  EMPTY_CHART_SUMMARIES,
  getCompletionStatus,
} from './dashboard.helpers';

const EMPTY_NOTIFICATIONS: INotification = { canConnect: false, entryBeans: [] };

type ChartLoader = (service: DashboardService) => Observable<IBasicChartConfigurations>;

/**
 * "My profile" chart summaries and the user's notifications. Root-scoped because they are read
 * outside the dashboard route too (notification bar, DFT history guard, post-submit dialogs).
 * Loads are commands; `DashboardService` stays pure HTTP.
 */
@Service()
export class DashboardState {
  private readonly _dashboardService = inject(DashboardService);

  private readonly _charts = signal<DashboardChartSummaries>(EMPTY_CHART_SUMMARIES);
  private readonly _notifications = signal<INotification>(EMPTY_NOTIFICATIONS);

  readonly charts: Signal<DashboardChartSummaries> = this._charts.asReadonly();
  readonly notifications: Signal<INotification> = this._notifications.asReadonly();

  readonly companyProfileSummary = computed(() => this._charts().companyProfileSummary);
  readonly ddqSummary = computed(() => this._charts().ddqSummary);
  readonly kyc = computed(() => this._charts().kyc);
  readonly kpi = computed(() => this._charts().kpi);
  readonly policyFiles = computed(() => this._charts().policyFiles);
  readonly reportingSummary = computed(() => this._charts().reportingSummary);
  readonly investorProfile = computed(() => this._charts().investorProfile);
  readonly pmKpiMyProfile = computed(() => this._charts().pmKpiMyProfile);

  readonly completionStatus: Signal<ICompletionStatus> = computed(() =>
    getCompletionStatus(this._charts())
  );

  loadCompanyProfileSummary(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('companyProfileSummary', (s) => s.getCompanyProfileSummary());
  }

  loadDDQSummary(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('ddqSummary', (s) => s.getDDQSummary());
  }

  loadKYC(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('kyc', (s) => s.getKYC());
  }

  loadKPI(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('kpi', (s) => s.getKPI());
  }

  loadPolicyFiles(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('policyFiles', (s) => s.getPolicy());
  }

  loadReportingSummary(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('reportingSummary', (s) => s.getReportingDashboardData());
  }

  loadInvestorProfile(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('investorProfile', (s) => s.getInvestorProfileSummary());
  }

  loadPmKpiMyProfile(): Observable<IBasicChartConfigurations | null> {
    return this._loadChart('pmKpiMyProfile', (s) => s.getPmKpiMyProfile());
  }

  /** Errors propagate: every caller decides how a failed notifications refresh is handled. */
  loadNotifications(): Observable<INotification> {
    return this._dashboardService
      .getNotifications()
      .pipe(tap((notifications) => this._notifications.set(notifications)));
  }

  /** A failed chart clears its summary and emits `null`, so sibling loads keep running. */
  private _loadChart(
    key: keyof DashboardChartSummaries,
    loader: ChartLoader
  ): Observable<IBasicChartConfigurations | null> {
    return loader(this._dashboardService).pipe(
      catchError(() => of(null)),
      tap((summary) => this._charts.update((charts) => ({ ...charts, [key]: summary })))
    );
  }
}
